import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Slider,
  Switch,
  Button,
  Snackbar,
  Alert,
  Paper,
  Grid,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import LockResetIcon from '@mui/icons-material/LockReset';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useNavigate } from 'react-router-dom';
import { useConfigStore } from '@/stores/useConfigStore';
import type { ModelConfig } from '@/types/config';
import {
  activateStageConfigVersion,
  createStageConfigVersion,
  getStageConfigVersions,
  getStageConfigs,
  type ConfigSide,
  type StageControlConfig,
  type StageControlLevel,
  type StageConfig,
  type StageConfigVersion,
} from '@/api/config';
import { createUser, deleteUser, listRoles, listUsers, saveRoles, updateUser } from '@/api/auth';
import { useAuthStore } from '@/stores/useAuthStore';
import type { PagePermission, RoleConfig, User } from '@/types/auth';
import {
  downloadTextFile,
  formatPromptText,
  formatStageControlText,
  parsePromptText,
  parseStageControlText,
  safeFilenamePart,
} from '@/utils/configTextFormat';

type TabValue = 'model' | 'prompt' | 'stage' | 'rag' | 'users';

const pageOptions = [
  { key: 'chat', label: '会话' },
  { key: 'config', label: '配置中心' },
  { key: 'prompt', label: 'System Prompt' },
  { key: 'stage', label: '会话阶段控制' },
  { key: 'rag', label: 'RAG' },
  { key: 'users', label: '用户' },
  { key: 'roles', label: '角色' },
];

const permissionOptions: PagePermission[] = ['none', 'read', 'write'];

const controlLevelOptions: Array<{ value: StageControlLevel; label: string; helper: string }> = [
  { value: 'force', label: '强制', helper: '严格遵循各阶段要求，满足当前阶段目标后才进入下一阶段。' },
  { value: 'strict', label: '严格', helper: '较严格遵循阶段要求，绝大部分条件满足时进入下一阶段。' },
  { value: 'normal', label: '普通', helper: '多数情况下遵守阶段要求，当前状态更适合下一阶段时可部分满足后推进。' },
  { value: 'suggested', label: '建议', helper: '阶段要求更宽松，鼓励遵循，但可以较早进入下一阶段。' },
  { value: 'flexible', label: '灵活', helper: '最宽松，阶段仅作为参考，系统灵活判断推进时机。' },
];

const emptyStage: StageConfig = {
  id: '',
  name: '',
  enabled: true,
  focus: '',
  prompt: '',
  conditions: { max_turns: '', force_advance_on_max_turns: false, intent: '', keywords: [], relation: 'or' },
  example_questions: [],
  children: [],
};

const defaultStageControl: StageControlConfig = {
  control_level: 'normal',
  stages: [],
};

const normalizeStageControl = (value: unknown): StageControlConfig => {
  if (value && typeof value === 'object' && Array.isArray((value as StageControlConfig).stages)) {
    return {
      control_level: ((value as StageControlConfig).control_level || 'normal') as StageControlLevel,
      stages: (value as StageControlConfig).stages.map((stage, index) => ({
        ...emptyStage,
        ...stage,
        id: stage.id || `stage_${index + 1}`,
        conditions: { ...emptyStage.conditions, ...stage.conditions },
        example_questions: stage.example_questions || [],
        children: (stage.children || []).map((child, childIndex) => ({
          ...emptyStage,
          ...child,
          id: child.id || `${stage.id || `stage_${index + 1}`}_sub_${childIndex + 1}`,
          conditions: { ...emptyStage.conditions, ...child.conditions },
          example_questions: child.example_questions || [],
          children: [],
        })),
      })),
    };
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, StageConfig>);
    return {
      control_level: 'normal',
      stages: entries
        .filter(([, stage]) => stage && typeof stage === 'object')
        .map(([id, stage]) => ({
          ...emptyStage,
          ...stage,
          id,
          conditions: { ...emptyStage.conditions, ...stage.conditions },
          example_questions: stage.example_questions || [],
          children: stage.children || [],
        })),
    };
  }

  return defaultStageControl;
};

const createBlankStage = (index: number, parentId?: string): StageConfig => ({
  ...emptyStage,
  id: parentId ? `${parentId}_sub_${index + 1}` : `stage_${Date.now()}_${index + 1}`,
  name: parentId ? '新子阶段' : '新阶段',
});

const splitList = (value: string): string[] =>
  value
    .split(/[，,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);

const ConfigPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabValue>('model');
  const [configSide, setConfigSide] = useState<ConfigSide>('left');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const promptFileInputRef = useRef<HTMLInputElement>(null);
  const stageFileInputRef = useRef<HTMLInputElement>(null);

  const leftConfig = useConfigStore((s) => s.leftConfig);
  const rightConfig = useConfigStore((s) => s.rightConfig);
  const systemPrompts = useConfigStore((s) => s.systemPrompts);
  const systemPromptVersionsBySide = useConfigStore((s) => s.systemPromptVersionsBySide);
  const promptVersionsBySide = useConfigStore((s) => s.promptVersionsBySide);
  const ragConfigs = useConfigStore((s) => s.ragConfigs);
  const loading = useConfigStore((s) => s.loading);
  const user = useAuthStore((s) => s.user);

  const storeUpdateModelConfig = useConfigStore((s) => s.updateModelConfig);
  const storeSavePromptVersion = useConfigStore((s) => s.savePromptVersion);
  const storeActivatePromptVersion = useConfigStore((s) => s.activatePromptVersion);
  const loadPromptVersions = useConfigStore((s) => s.loadPromptVersions);
  const storeToggleRag = useConfigStore((s) => s.toggleRag);
  const storeUpdateRagTopK = useConfigStore((s) => s.updateRagTopK);
  const loadConfig = useConfigStore((s) => s.loadConfig);
  const systemPrompt = systemPrompts[configSide] || '';
  const systemPromptVersion = systemPromptVersionsBySide[configSide] || 'coach_v1';
  const promptVersions = promptVersionsBySide[configSide] || [];
  const ragConfig = ragConfigs[configSide];

  const [localLeft, setLocalLeft] = useState<ModelConfig>(leftConfig);
  const [localRight, setLocalRight] = useState<ModelConfig>(rightConfig);
  const [localPrompt, setLocalPrompt] = useState(systemPrompt);
  const [localPromptName, setLocalPromptName] = useState('');
  const [isPromptEditing, setIsPromptEditing] = useState(false);
  const [isCreatingPromptVersion, setIsCreatingPromptVersion] = useState(false);
  const [localRagEnabled, setLocalRagEnabled] = useState(ragConfig.enabled);
  const [localRagTopK, setLocalRagTopK] = useState(ragConfig.top_k);
  const [stageControl, setStageControl] = useState<StageControlConfig>(defaultStageControl);
  const [stageVersions, setStageVersions] = useState<StageConfigVersion[]>([]);
  const [stageVersionName, setStageVersionName] = useState('');
  const [isStageEditing, setIsStageEditing] = useState(false);
  const [isCreatingStageVersion, setIsCreatingStageVersion] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<RoleConfig[]>([]);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    display_name: '',
    role: 'user',
    tenant_id: 'default',
  });
  const [resetTarget, setResetTarget] = useState<User | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const canManageUsers = user?.role === 'admin';

  const showSuccess = (msg: string) => {
    setSnackbarSeverity('success');
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  };

  const showError = (msg: string) => {
    setSnackbarSeverity('error');
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  };

  const loadStages = useCallback(async () => {
    const [configs, versions] = await Promise.all([getStageConfigs(configSide), getStageConfigVersions(configSide)]);
    setStageControl(normalizeStageControl(configs));
    setStageVersions(versions);
  }, [configSide]);

  const loadUsersAndRoles = useCallback(async () => {
    if (!canManageUsers) {
      return;
    }
    const [nextUsers, nextRoles] = await Promise.all([listUsers(), listRoles()]);
    setUsers(nextUsers);
    setRoles(nextRoles);
  }, [canManageUsers]);

  useEffect(() => {
    loadConfig();
    loadPromptVersions(configSide);
    loadStages().catch(() => {
      setStageControl(defaultStageControl);
      setStageVersions([]);
    });
  }, [configSide, loadConfig, loadPromptVersions, loadStages]);

  useEffect(() => {
    loadUsersAndRoles().catch(() => {
      setUsers([]);
      setRoles([]);
    });
  }, [loadUsersAndRoles]);

  useEffect(() => {
    setLocalLeft(leftConfig);
    setLocalRight(rightConfig);
    setLocalPrompt(systemPrompt);
    setLocalRagEnabled(ragConfig.enabled);
    setLocalRagTopK(ragConfig.top_k);
    setLocalPromptName('');
    setIsPromptEditing(false);
    setIsCreatingPromptVersion(false);
    setStageVersionName('');
    setIsStageEditing(false);
    setIsCreatingStageVersion(false);
  }, [leftConfig, rightConfig, systemPrompt, ragConfig.enabled, ragConfig.top_k, configSide]);

  const handleTabChange = (_: React.SyntheticEvent, value: TabValue) => {
    setActiveTab(value);
  };

  const handleSaveModelConfig = useCallback(async () => {
    await storeUpdateModelConfig('left', localLeft);
    await storeUpdateModelConfig('right', localRight);
    showSuccess('模型配置已保存');
  }, [localLeft, localRight, storeUpdateModelConfig]);

  const handleSavePrompt = useCallback(async () => {
    if (!isPromptEditing || !localPrompt.trim() || (isCreatingPromptVersion && !localPromptName.trim())) {
      return;
    }
    await storeSavePromptVersion(localPrompt, localPromptName || undefined, configSide);
    setLocalPromptName('');
    setIsPromptEditing(false);
    setIsCreatingPromptVersion(false);
    showSuccess(isCreatingPromptVersion ? 'System Prompt 新版本已创建' : 'System Prompt 已保存');
  }, [configSide, isCreatingPromptVersion, isPromptEditing, localPrompt, localPromptName, storeSavePromptVersion]);

  const handleActivatePrompt = useCallback(
    async (version: string) => {
      if (isPromptEditing) {
        return;
      }
      await storeActivatePromptVersion(version, configSide);
      showSuccess('已切换 System Prompt 版本');
    },
    [configSide, isPromptEditing, storeActivatePromptVersion]
  );

  const handleEditPrompt = useCallback(() => {
    setIsPromptEditing(true);
    setIsCreatingPromptVersion(false);
    setLocalPromptName('');
  }, []);

  const handleNewPromptVersion = useCallback(() => {
    setLocalPrompt('');
    setLocalPromptName('');
    setIsPromptEditing(true);
    setIsCreatingPromptVersion(true);
  }, []);

  const handleCancelPromptEdit = useCallback(() => {
    setLocalPrompt(systemPrompt);
    setLocalPromptName('');
    setIsPromptEditing(false);
    setIsCreatingPromptVersion(false);
  }, [systemPrompt]);

  const handleExportPrompt = useCallback(() => {
    const activeVersion = promptVersions.find((item) => item.active);
    const name = localPromptName.trim() || activeVersion?.name || systemPromptVersion;
    const text = isPromptEditing ? localPrompt : systemPrompt;
    downloadTextFile(`deepflow-system-prompt-${configSide}-${safeFilenamePart(name)}.txt`, formatPromptText(text, name, configSide));
    showSuccess('System Prompt 已导出为文本文件');
  }, [configSide, isPromptEditing, localPrompt, localPromptName, promptVersions, systemPrompt, systemPromptVersion]);

  const handleImportPrompt = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const imported = parsePromptText(await file.text());
      setLocalPrompt(imported.text);
      setLocalPromptName(imported.name || `导入 Prompt ${new Date().toLocaleString('zh-CN', { hour12: false })}`);
      setIsPromptEditing(true);
      setIsCreatingPromptVersion(true);
      showSuccess('System Prompt 已导入，请确认内容后保存为新版本');
    } catch (error) {
      showError(error instanceof Error ? error.message : 'System Prompt 导入失败');
    }
  }, []);

  const handleSaveRag = useCallback(async () => {
    await storeToggleRag(localRagEnabled, configSide);
    await storeUpdateRagTopK(localRagTopK, configSide);
    showSuccess('RAG 配置已保存');
  }, [configSide, localRagEnabled, localRagTopK, storeToggleRag, storeUpdateRagTopK]);

  const handleSaveStages = useCallback(async () => {
    if (!isStageEditing || (isCreatingStageVersion && !stageVersionName.trim())) {
      return;
    }
    await createStageConfigVersion(stageControl, stageVersionName || undefined, configSide);
    setStageVersionName('');
    await loadStages();
    setIsStageEditing(false);
    setIsCreatingStageVersion(false);
    showSuccess(isCreatingStageVersion ? '会话阶段控制新版本已创建' : '会话阶段控制已保存');
  }, [configSide, isCreatingStageVersion, isStageEditing, loadStages, stageControl, stageVersionName]);

  const handleActivateStage = useCallback(
    async (version: string) => {
      if (isStageEditing) {
        return;
      }
      const activated = await activateStageConfigVersion(version, configSide);
      setStageControl(normalizeStageControl(activated.stage_control || activated.stages || defaultStageControl));
      await loadStages();
      showSuccess('已切换会话阶段控制版本');
    },
    [configSide, isStageEditing, loadStages]
  );

  const handleEditStages = useCallback(() => {
    setIsStageEditing(true);
    setIsCreatingStageVersion(false);
  }, []);

  const handleNewStageVersion = useCallback(() => {
    setStageControl(defaultStageControl);
    setStageVersionName('');
    setIsStageEditing(true);
    setIsCreatingStageVersion(true);
  }, []);

  const handleCancelStageEdit = useCallback(async () => {
    await loadStages();
    setStageVersionName('');
    setIsStageEditing(false);
    setIsCreatingStageVersion(false);
  }, [loadStages]);

  const handleExportStages = useCallback(() => {
    const activeVersion = stageVersions.find((item) => item.active);
    const name = stageVersionName.trim() || activeVersion?.name || '会话阶段控制';
    downloadTextFile(
      `deepflow-stage-control-${configSide}-${safeFilenamePart(name)}.txt`,
      formatStageControlText(stageControl, name, configSide)
    );
    showSuccess('会话阶段控制已导出为文本文件');
  }, [configSide, stageControl, stageVersionName, stageVersions]);

  const handleImportStages = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const imported = parseStageControlText(await file.text());
      setStageControl(normalizeStageControl(imported.stageControl));
      setStageVersionName(imported.name || `导入阶段控制 ${new Date().toLocaleString('zh-CN', { hour12: false })}`);
      setIsStageEditing(true);
      setIsCreatingStageVersion(true);
      showSuccess('会话阶段控制已导入，请确认内容后保存为新版本');
    } catch (error) {
      showError(error instanceof Error ? error.message : '会话阶段控制导入失败');
    }
  }, []);

  const handleCreateUser = useCallback(async () => {
    await createUser({ ...newUser, permissions: [] });
    setNewUser({ username: '', password: '', display_name: '', role: 'user', tenant_id: 'default' });
    await loadUsersAndRoles();
    showSuccess('用户已创建');
  }, [loadUsersAndRoles, newUser]);

  const handleResetPassword = useCallback(async () => {
    if (!resetTarget || resetPassword.length < 8) {
      return;
    }
    setResetBusy(true);
    try {
      await updateUser(resetTarget.user_id, { password: resetPassword });
      showSuccess(`用户 ${resetTarget.username} 的密码已重置`);
      setResetTarget(null);
      setResetPassword('');
    } catch (error) {
      showError(error instanceof Error ? error.message : '密码重置失败');
    } finally {
      setResetBusy(false);
    }
  }, [resetTarget, resetPassword]);

  const handleDeleteUser = useCallback(async () => {
    if (!deleteTarget) {
      return;
    }
    setDeleteBusy(true);
    try {
      // Backend safety gate: only disabled users can be deleted — disable
      // first when the account is still active (single admin confirmation
      // here replaces the hidden two-step flow).
      if (deleteTarget.is_active) {
        await updateUser(deleteTarget.user_id, { is_active: false });
      }
      await deleteUser(deleteTarget.user_id);
      await loadUsersAndRoles();
      showSuccess(`用户 ${deleteTarget.username} 已删除，其历史会话记录已保留`);
      setDeleteTarget(null);
    } catch (error) {
      showError(error instanceof Error ? error.message : '用户删除失败');
    } finally {
      setDeleteBusy(false);
    }
  }, [deleteTarget, loadUsersAndRoles]);

  const handleSaveRoles = useCallback(async () => {
    const saved = await saveRoles(roles);
    setRoles(saved);
    showSuccess('角色权限已保存');
  }, [roles]);

  const renderModelForm = (
    label: string,
    config: ModelConfig,
    onChange: (cfg: ModelConfig) => void
  ) => (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        {label}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField fullWidth label="名称" value={config.name} onChange={(e) => onChange({ ...config, name: e.target.value })} size="small" />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label="API Base URL" value={config.apiBaseUrl} onChange={(e) => onChange({ ...config, apiBaseUrl: e.target.value })} size="small" />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label="API Key" value={config.apiKey} onChange={(e) => onChange({ ...config, apiKey: e.target.value })} size="small" type="password" />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label="Model" value={config.model} onChange={(e) => onChange({ ...config, model: e.target.value })} size="small" />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Temperature: {config.temperature}
          </Typography>
          <Slider value={config.temperature} onChange={(_, value) => onChange({ ...config, temperature: value as number })} min={0} max={2} step={0.1} marks valueLabelDisplay="auto" />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Max Tokens: {config.maxTokens}
          </Typography>
          <Slider
            value={config.maxTokens}
            onChange={(_, value) => onChange({ ...config, maxTokens: value as number })}
            min={256}
            max={8192}
            step={256}
            marks={[
              { value: 256, label: '256' },
              { value: 4096, label: '4K' },
              { value: 8192, label: '8K' },
            ]}
            valueLabelDisplay="auto"
          />
        </Grid>
      </Grid>
    </Paper>
  );

  const updateStage = (index: number, patch: Partial<StageConfig>, childIndex?: number) => {
    setStageControl((prev) => ({
      ...prev,
      stages: prev.stages.map((stage, i) => {
        if (i !== index) {
          return stage;
        }
        if (childIndex === undefined) {
          return { ...emptyStage, ...stage, ...patch };
        }
        return {
          ...stage,
          children: (stage.children || []).map((child, j) =>
            j === childIndex ? { ...emptyStage, ...child, ...patch, children: [] } : child
          ),
        };
      }),
    }));
  };

  const updateStageCondition = (index: number, patch: Partial<StageConfig['conditions']>, childIndex?: number) => {
    setStageControl((prev) => ({
      ...prev,
      stages: prev.stages.map((stage, i) => {
        if (i !== index) {
          return stage;
        }
        if (childIndex === undefined) {
          return {
            ...emptyStage,
            ...stage,
            conditions: { ...emptyStage.conditions, ...stage.conditions, ...patch },
          };
        }
        return {
          ...stage,
          children: (stage.children || []).map((child, j) =>
            j === childIndex
              ? {
                  ...emptyStage,
                  ...child,
                  children: [],
                  conditions: { ...emptyStage.conditions, ...child.conditions, ...patch },
                }
              : child
          ),
        };
      }),
    }));
  };

  const addStage = () => {
    setStageControl((prev) => ({
      ...prev,
      stages: [...prev.stages, createBlankStage(prev.stages.length)],
    }));
  };

  const deleteStage = (index: number) => {
    setStageControl((prev) => ({
      ...prev,
      stages: prev.stages.filter((_, i) => i !== index),
    }));
  };

  const addChildStage = (index: number) => {
    setStageControl((prev) => ({
      ...prev,
      stages: prev.stages.map((stage, i) =>
        i === index
          ? {
              ...stage,
              children: [...(stage.children || []), createBlankStage(stage.children?.length || 0, stage.id)],
            }
          : stage
      ),
    }));
  };

  const deleteChildStage = (index: number, childIndex: number) => {
    setStageControl((prev) => ({
      ...prev,
      stages: prev.stages.map((stage, i) =>
        i === index
          ? {
              ...stage,
              children: (stage.children || []).filter((_, j) => j !== childIndex),
            }
          : stage
      ),
    }));
  };

  const renderStageEditor = (stage: StageConfig, index: number, childIndex?: number) => {
    const item = { ...emptyStage, ...stage };
    const conditions = { ...emptyStage.conditions, ...item.conditions };
    const isChild = childIndex !== undefined;
    const disabled = !isStageEditing;
    return (
      <Paper
        variant="outlined"
        sx={{ p: 2, borderRadius: 1, bgcolor: isChild ? 'action.hover' : 'background.paper' }}
        key={`${item.id}-${index}-${childIndex ?? 'parent'}`}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField disabled={disabled} fullWidth size="small" label="阶段标识" value={item.id} onChange={(e) => updateStage(index, { id: e.target.value }, childIndex)} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField disabled={disabled} fullWidth size="small" label="阶段名称" value={item.name} onChange={(e) => updateStage(index, { name: e.target.value }, childIndex)} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              disabled={disabled}
              select
              fullWidth
              size="small"
              label="条件关系"
              value={conditions.relation || 'or'}
              onChange={(e) => updateStageCondition(index, { relation: e.target.value as 'or' | 'and' }, childIndex)}
            >
              <MenuItem value="or">或</MenuItem>
              <MenuItem value="and">且</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
              <Typography variant="body2">{item.enabled ? '已开启' : '已关闭'}</Typography>
              <Switch disabled={disabled} checked={item.enabled} onChange={(e) => updateStage(index, { enabled: e.target.checked }, childIndex)} />
              <Tooltip title={isChild ? '删除子阶段' : '删除阶段'}>
                <span>
                <IconButton disabled={disabled} color="error" onClick={() => (isChild ? deleteChildStage(index, childIndex ?? 0) : deleteStage(index))}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              disabled={disabled}
              fullWidth
              multiline
              minRows={2}
              label="阶段侧重点"
              value={item.focus || ''}
              onChange={(e) => updateStage(index, { focus: e.target.value }, childIndex)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              disabled={disabled}
              fullWidth
              multiline
              minRows={3}
              label="阶段提示词"
              value={item.prompt}
              onChange={(e) => updateStage(index, { prompt: e.target.value }, childIndex)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              disabled={disabled}
              fullWidth
              size="small"
              type="number"
              label="最大会话轮次"
              value={conditions.max_turns ?? ''}
              onChange={(e) => updateStageCondition(index, { max_turns: e.target.value === '' ? '' : Number(e.target.value) }, childIndex)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1 }}>
              <Switch
                disabled={disabled || !conditions.max_turns}
                checked={Boolean(conditions.force_advance_on_max_turns)}
                onChange={(e) => updateStageCondition(index, { force_advance_on_max_turns: e.target.checked }, childIndex)}
              />
              <Typography variant="body2">超过轮次强制进入下一阶段</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              disabled={disabled}
              fullWidth
              size="small"
              label="关键字"
              value={(conditions.keywords || []).join('，')}
              onChange={(e) => updateStageCondition(index, { keywords: splitList(e.target.value) }, childIndex)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              disabled={disabled}
              fullWidth
              size="small"
              label="用户意图判断描述"
              value={conditions.intent || ''}
              onChange={(e) => updateStageCondition(index, { intent: e.target.value }, childIndex)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              disabled={disabled}
              fullWidth
              multiline
              minRows={2}
              label="示例问句"
              value={(item.example_questions || []).join('\n')}
              onChange={(e) => updateStage(index, { example_questions: splitList(e.target.value) }, childIndex)}
            />
          </Grid>
          {!isChild && (
            <Grid item xs={12}>
              <Button disabled={disabled} size="small" startIcon={<AddIcon />} onClick={() => addChildStage(index)}>
                增加子阶段
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>
    );
  };

  const updateRole = (index: number, patch: Partial<RoleConfig>) => {
    setRoles((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const updateRolePermission = (index: number, page: string, permission: PagePermission) => {
    setRoles((prev) =>
      prev.map((item, i) =>
        i === index
          ? { ...item, permissions: { ...item.permissions, [page]: permission } }
          : item
      )
    );
  };

  const estimatedTokens = Math.ceil(
    localPrompt.split('').reduce((acc, char) => acc + (char.charCodeAt(0) > 127 ? 1.5 : 0.25), 0)
  );
  const sideName = configSide === 'left' ? '左侧大模型' : '右侧大模型';
  const renderSideSelector = () => (
    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
      <Button
        size="small"
        variant={configSide === 'left' ? 'contained' : 'outlined'}
        onClick={() => setConfigSide('left')}
        disabled={isPromptEditing || isStageEditing}
      >
        左侧大模型
      </Button>
      <Button
        size="small"
        variant={configSide === 'right' ? 'contained' : 'outlined'}
        onClick={() => setConfigSide('right')}
        disabled={isPromptEditing || isStageEditing}
      >
        右侧大模型
      </Button>
    </Stack>
  );

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/chat')}>
          返回
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
          配置中心
        </Typography>
      </Box>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="模型配置" value="model" />
        <Tab label="System Prompt" value="prompt" />
        <Tab label="会话阶段控制" value="stage" />
        <Tab label="RAG 配置" value="rag" />
        {canManageUsers && <Tab label="用户权限" value="users" />}
      </Tabs>

      <input ref={promptFileInputRef} hidden type="file" accept=".txt,.md,text/plain" onChange={handleImportPrompt} />
      <input ref={stageFileInputRef} hidden type="file" accept=".txt,.md,text/plain" onChange={handleImportStages} />

      {activeTab === 'model' && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              {renderModelForm('左栏模型', localLeft, setLocalLeft)}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderModelForm('右栏模型', localRight, setLocalRight)}
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveModelConfig} disabled={loading}>
              保存模型配置
            </Button>
          </Box>
        </Box>
      )}

      {activeTab === 'stage' && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            {renderSideSelector()}
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, mb: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {isCreatingStageVersion ? `新建${sideName}会话阶段控制版本` : `${sideName}会话阶段控制`}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {isStageEditing ? '编辑模式：完成后点击保存生成当前配置版本。' : '查看模式：点击编辑后可修改当前配置。'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    {!isStageEditing && (
                      <>
                        <Button variant="outlined" startIcon={<FileUploadIcon />} onClick={() => stageFileInputRef.current?.click()}>
                          导入文本
                        </Button>
                        <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportStages}>
                          导出文本
                        </Button>
                        <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEditStages}>
                          编辑
                        </Button>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewStageVersion}>
                          新建版本
                        </Button>
                      </>
                    )}
                    {isStageEditing && (
                      <>
                        <Button variant="outlined" onClick={handleCancelStageEdit}>
                          取消
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={handleSaveStages}
                          disabled={isCreatingStageVersion && !stageVersionName.trim()}
                        >
                          保存
                        </Button>
                      </>
                    )}
                  </Stack>
                </Box>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <TextField
                      disabled={!isStageEditing}
                      select
                      fullWidth
                      size="small"
                      label="控制程度"
                      value={stageControl.control_level}
                      onChange={(e) => setStageControl((prev) => ({ ...prev, control_level: e.target.value as StageControlLevel }))}
                    >
                      {controlLevelOptions.map((option) => (
                        <MenuItem value={option.value} key={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {controlLevelOptions.find((option) => option.value === stageControl.control_level)?.helper}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
              {stageControl.stages.map((stage, index) => (
                <Stack spacing={1.5} key={`${stage.id}-${index}`}>
                  {renderStageEditor(stage, index)}
                  {(stage.children || []).map((child, childIndex) => (
                    <Box sx={{ pl: { xs: 0, md: 3 } }} key={`${child.id}-${childIndex}`}>
                      {renderStageEditor(child, index, childIndex)}
                    </Box>
                  ))}
                </Stack>
              ))}
              <Button disabled={!isStageEditing} variant="outlined" startIcon={<AddIcon />} onClick={addStage}>
                增加阶段
              </Button>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <TextField
                      disabled={!isStageEditing}
                      required={isCreatingStageVersion}
                      fullWidth
                      size="small"
                      label="版本名称"
                      helperText={isCreatingStageVersion ? '新建版本需要填写版本名称' : '编辑当前配置时可填写版本名称；为空则自动生成'}
                      value={stageVersionName}
                      onChange={(e) => setStageVersionName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveStages}
                      disabled={!isStageEditing || (isCreatingStageVersion && !stageVersionName.trim())}
                    >
                      保存
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {sideName}阶段控制版本
              </Typography>
              <List dense sx={{ maxHeight: 620, overflow: 'auto' }}>
                {stageVersions.map((item) => (
                  <ListItemButton
                    disabled={isStageEditing}
                    key={item.version}
                    selected={item.active}
                    onClick={() => handleActivateStage(item.version)}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemText primary={item.name || item.version} secondary={`${item.active ? '当前版本 · ' : ''}${item.hash || item.version}`} />
                  </ListItemButton>
                ))}
                {stageVersions.length === 0 && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
                    暂无历史版本
                  </Typography>
                )}
              </List>
            </Paper>
          </Grid>
        </Grid>
      )}

      {activeTab === 'prompt' && (
        <Box>
          {renderSideSelector()}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, mb: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {isCreatingPromptVersion ? `新建${sideName} System Prompt 版本` : `${sideName} System Prompt`}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {isPromptEditing ? '编辑模式：完成后点击保存生成当前 Prompt 版本。' : '查看模式：点击编辑后可修改当前 Prompt。'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    {!isPromptEditing && (
                      <>
                        <Button variant="outlined" startIcon={<FileUploadIcon />} onClick={() => promptFileInputRef.current?.click()}>
                          导入文本
                        </Button>
                        <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportPrompt}>
                          导出文本
                        </Button>
                        <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEditPrompt}>
                          编辑
                        </Button>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewPromptVersion}>
                          新建版本
                        </Button>
                      </>
                    )}
                    {isPromptEditing && (
                      <>
                        <Button variant="outlined" onClick={handleCancelPromptEdit}>
                          取消
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={handleSavePrompt}
                          disabled={!localPrompt.trim() || (isCreatingPromptVersion && !localPromptName.trim())}
                        >
                          保存
                        </Button>
                      </>
                    )}
                  </Stack>
                </Box>
                <TextField
                  disabled={!isPromptEditing}
                  required={isCreatingPromptVersion}
                  fullWidth
                  size="small"
                  label="版本名称"
                  helperText={isCreatingPromptVersion ? '新建版本需要填写版本名称' : '编辑当前 Prompt 时可填写版本名称；为空则自动生成'}
                  value={localPromptName}
                  onChange={(e) => setLocalPromptName(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  disabled={!isPromptEditing}
                  fullWidth
                  multiline
                  rows={12}
                  label="System Prompt"
                  value={localPrompt}
                  onChange={(e) => setLocalPrompt(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'monospace', fontSize: 14 } }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, color: 'text.secondary', fontSize: 12 }}>
                  <span>当前版本: {systemPromptVersion}</span>
                  <span>字符数: {localPrompt.length} / 估算 Token 数: ~{estimatedTokens}</span>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  历史版本
                </Typography>
                <List dense sx={{ maxHeight: 430, overflow: 'auto' }}>
                  {promptVersions.map((item) => (
                    <ListItemButton
                      disabled={isPromptEditing}
                      key={item.version}
                      selected={item.active}
                      onClick={() => {
                        setLocalPrompt(item.text);
                        handleActivatePrompt(item.version);
                      }}
                      sx={{ borderRadius: 1, mb: 0.5 }}
                    >
                      <ListItemText primary={item.name || item.version} secondary={`${item.active ? '当前版本 · ' : ''}${item.hash || item.version}`} />
                    </ListItemButton>
                  ))}
                  {promptVersions.length === 0 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
                      暂无历史版本
                    </Typography>
                  )}
                </List>
              </Paper>
            </Grid>
          </Grid>
          {isPromptEditing && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSavePrompt}
                disabled={loading || !localPrompt.trim() || (isCreatingPromptVersion && !localPromptName.trim())}
              >
                保存
              </Button>
            </Box>
          )}
        </Box>
      )}

      {activeTab === 'users' && canManageUsers && (
        <Stack spacing={3}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
              创建用户
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <TextField fullWidth size="small" label="用户名" value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="初始密码"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  error={newUser.password.length > 0 && newUser.password.length < 8}
                  helperText={newUser.password.length > 0 && newUser.password.length < 8 ? "密码至少 8 位（当前 " + newUser.password.length + " 位）" : "至少 8 位"}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField fullWidth size="small" label="显示名" value={newUser.display_name} onChange={(e) => setNewUser({ ...newUser, display_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField select fullWidth size="small" label="角色" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                  {roles.map((role) => (
                    <MenuItem value={role.role} key={role.role}>
                      {role.name || role.role}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField fullWidth size="small" label="租户" value={newUser.tenant_id} onChange={(e) => setNewUser({ ...newUser, tenant_id: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button fullWidth variant="contained" onClick={handleCreateUser} disabled={!newUser.username || newUser.password.length < 8}>
                  创建
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                角色权限
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  setRoles((prev) => [
                    ...prev,
                    {
                      role: `role_${prev.length + 1}`,
                      name: '新角色',
                      permissions: Object.fromEntries(pageOptions.map((page) => [page.key, page.key === 'chat' ? 'write' : 'none'])) as RoleConfig['permissions'],
                    },
                  ])
                }
              >
                新增角色
              </Button>
            </Box>
            <Stack spacing={2}>
              {roles.map((role, index) => (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }} key={`${role.role}-${index}`}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth size="small" label="角色标识" value={role.role} disabled={role.system} onChange={(e) => updateRole(index, { role: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth size="small" label="角色名称" value={role.name} onChange={(e) => updateRole(index, { name: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Grid container spacing={1}>
                        {pageOptions.map((page) => (
                          <Grid item xs={6} md={3} key={page.key}>
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label={page.label}
                              value={role.permissions?.[page.key] || 'none'}
                              onChange={(e) => updateRolePermission(index, page.key, e.target.value as PagePermission)}
                            >
                              {permissionOptions.map((permission) => (
                                <MenuItem value={permission} key={permission}>
                                  {permission}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Stack>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveRoles}>
                保存角色权限
              </Button>
            </Box>
          </Paper>

          <Grid container spacing={2}>
            {users.map((item) => (
              <Grid item xs={12} md={6} key={item.user_id}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {item.username}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {item.display_name} · {item.tenant_id}
                  </Typography>
                  <Grid container spacing={1} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={5}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="角色"
                        value={item.role}
                        onChange={async (e) => {
                          await updateUser(item.user_id, { role: e.target.value });
                          await loadUsersAndRoles();
                        }}
                      >
                        {roles.map((role) => (
                          <MenuItem value={role.role} key={role.role}>
                            {role.name || role.role}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                          size="small"
                          variant={item.is_active ? 'outlined' : 'contained'}
                          onClick={async () => {
                            await updateUser(item.user_id, { is_active: !item.is_active });
                            await loadUsersAndRoles();
                          }}
                        >
                          {item.is_active ? '禁用' : '启用'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LockResetIcon />}
                          onClick={() => {
                            setResetTarget(item);
                            setResetPassword('');
                          }}
                        >
                          重置密码
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<DeleteIcon />}
                          onClick={() => setDeleteTarget(item)}
                        >
                          删除
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Stack>
      )}

      {activeTab === 'rag' && (
        <Box>
          {renderSideSelector()}
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Switch checked={localRagEnabled} onChange={(e) => setLocalRagEnabled(e.target.checked)} />
              <Typography variant="body1">{sideName}启用 RAG 检索增强</Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ opacity: localRagEnabled ? 1 : 0.5, pointerEvents: localRagEnabled ? 'auto' : 'none' }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Top-K 检索数量: {localRagTopK}
              </Typography>
              <Slider
                value={localRagTopK}
                onChange={(_, value) => setLocalRagTopK(value as number)}
                min={1}
                max={20}
                step={1}
                marks={[
                  { value: 1, label: '1' },
                  { value: 10, label: '10' },
                  { value: 20, label: '20' },
                ]}
                valueLabelDisplay="auto"
              />
            </Box>
          </Paper>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveRag} disabled={loading}>
              保存 RAG 配置
            </Button>
          </Box>
        </Box>
      )}

      <Dialog open={resetTarget !== null} onClose={() => !resetBusy && setResetTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>重置密码</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            为用户 <b>{resetTarget?.username}</b> 设置新的登录密码，重置后旧密码立即失效。
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="新密码"
            type="password"
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
            error={resetPassword.length > 0 && resetPassword.length < 8}
            helperText={resetPassword.length > 0 && resetPassword.length < 8 ? "密码至少 8 位（当前 " + resetPassword.length + " 位）" : "至少 8 位"}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetTarget(null)} disabled={resetBusy}>
            取消
          </Button>
          <Button variant="contained" onClick={handleResetPassword} disabled={resetBusy || resetPassword.length < 8}>
            确认重置
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteTarget !== null} onClose={() => !deleteBusy && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>删除用户</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            确定删除用户 <b>{deleteTarget?.username}</b>（{deleteTarget?.display_name}）吗？此操作不可恢复。
          </Typography>
          <Typography variant="body2" color="text.secondary">
            该用户的历史会话记录将保留，不会被删除。
            {deleteTarget?.is_active && ' 该用户当前处于启用状态，删除前将自动禁用。'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>
            取消
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteUser} disabled={deleteBusy}>
            确认删除
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConfigPage;
